import { Endpoints, makeAuthDeleteRequest, makeAuthGetRequest, makeAuthPutRequest } from "../../network/http-client";
export var RegistrationChange;
(function (RegistrationChange) {
    RegistrationChange["block"] = "BLOCK";
    RegistrationChange["unblock"] = "UNBLOCK";
    RegistrationChange["remove"] = "REMOVE";
})(RegistrationChange || (RegistrationChange = {}));
export class RegistrationService {
    registrationStore;
    logStore;
    PAGE_SIZE = 50; // default size of entries per API request
    constructor(registrationStore, logStore) {
        this.registrationStore = registrationStore;
        this.logStore = logStore;
    }
    // This method starts loading from page 0 and sets the registrations instead of appending
    async getRegistrationsList(userId, inactive = false, logOnSuccess = false) {
        try {
            const pageNumber = 0;
            const registrations = await this.fetchRegistrations(userId, String(inactive), pageNumber, this.PAGE_SIZE);
            const allEntriesLoaded = registrations.length < this.PAGE_SIZE;
            this.registrationStore.setRegistrations(registrations, allEntriesLoaded);
            if (logOnSuccess) {
                this.logStore.logSuccess("Registrations reloaded successfully.", "registrations.reloadedOk");
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    async getNextPage(userId, inactive = false) {
        try {
            const pageNumber = this.registrationStore.pageNumber + 1;
            const registrations = await this.fetchRegistrations(userId, String(inactive), pageNumber, this.PAGE_SIZE);
            const allEntriesLoaded = registrations.length < this.PAGE_SIZE;
            this.registrationStore.appendRegistrations(registrations, allEntriesLoaded, pageNumber);
        }
        catch (error) {
            console.error(error);
        }
    }
    async reloadRegistration(regId, logOnSuccess = false) {
        try {
            const isHistoryLoaded = this.registrationStore.history != null;
            const registration = await this.fetchRegistration(regId);
            this.registrationStore.setRegistration(registration);
            // load history again, if it was previously loaded as well
            if (isHistoryLoaded) {
                await this.getRegistrationHistory(regId);
            }
            if (logOnSuccess) {
                this.logStore.logSuccess("Registration reloaded successfully.", "registrations.detail.reloadedOk");
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    async deleteRegistration(regId) {
        try {
            const result = await this.removeRegistration(regId);
            if (result.status === "OK") {
                this.logStore.logSuccess("Registration deleted successfully.", "registrations.detail.deletedOk");
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    async changeRegistrationState(change, regId, externalUserId, blockReason) {
        try {
            const result = await this.changeRegistration(change, regId, externalUserId, blockReason);
            if (result.status === "OK") {
                this.logStore.logSuccess("Registration changed successfully.", "registrations.detail.changedOk");
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    async getRegistrationHistory(regId) {
        try {
            const history = await this.fetchRegistrationHistory(regId);
            this.registrationStore.setHistory(history);
        }
        catch (error) {
            console.error(error);
        }
    }
    // PRIVATE - in error case, log and reject promise
    async fetchRegistrations(userId, removed, pageNumber, pageSize) {
        // TODO: make sure userId is sanitized
        const res = await makeAuthGetRequest(Endpoints.registrations, { userId, removed, pageSize: `${pageSize}`, pageNumber: `${pageNumber}` });
        if (res.response) {
            return res.response.registrations;
        }
        else {
            // error path
            this.logStore.logResponseError(res, this.fetchRegistrations.name);
            return Promise.reject(new Error("Fetch registrations failed. For more see Error Logs."));
        }
    }
    async fetchRegistration(regId) {
        const res = await makeAuthGetRequest(`${Endpoints.registrations}/${regId}`, {});
        if (res.response) {
            return res.response;
        }
        // error path
        this.logStore.logResponseError(res, this.fetchRegistration.name);
        return Promise.reject(new Error("Fetch registration failed. For more see Error Logs."));
    }
    async removeRegistration(regId) {
        const res = await makeAuthDeleteRequest(`${Endpoints.registrations}/${regId}`);
        if (res.response) {
            return res.response;
        }
        // error path
        this.logStore.logResponseError(res, this.removeRegistration.name);
        return Promise.reject(new Error("Fetch registrations failed. For more see Error Logs."));
    }
    async changeRegistration(change, regId, externalUserId, blockReason) {
        const payload = {
            change,
            ...(externalUserId && { externalUserId }),
            ...(blockReason && { blockReason })
        };
        const res = await makeAuthPutRequest(`${Endpoints.registrations}/${regId}`, {}, payload);
        if (res.response) {
            return res.response;
        }
        // error path
        this.logStore.logResponseError(res, this.changeRegistration.name);
        return Promise.reject(new Error("Fetch registrations failed. For more see Error Logs."));
    }
    async fetchRegistrationHistory(regId) {
        const res = await makeAuthGetRequest(`${Endpoints.registrations}/${regId}/history`, {});
        if (res.response) {
            return res.response.registrations;
        }
        // error path
        this.logStore.logResponseError(res, this.fetchRegistrationHistory.name);
        return Promise.reject(new Error("Fetch registrations failed. For more see Error Logs."));
    }
}
